import clsx from 'clsx'
import { Link } from 'react-router-dom'
import React, { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

export type ButtonVariant =
  | 'primary'
  | 'outlined'
  | 'neutral'
  | 'danger'
  | 'link'
  // white on a dark banner
  | 'secondary'
  // dark fill
  | 'accent'
  // kept for old call sites: the primary button at the large size
  | 'large'
  | 'empty'

export type ButtonSize = 'sm' | 'md' | 'lg'

type Props = {
  icon?: JSX.Element
  iconAfter?: JSX.Element
  link?: string
  disabled?: boolean
  isPersonal?: boolean
  className?: string
  classNameWrapper?: string
  variant?: ButtonVariant
  size?: ButtonSize
  type?: 'submit' | 'reset' | 'button'
  onClick?: (...e: any) => void
  target?: string
  rel?: string
  iconClassName?: string
  isLoading?: boolean
}

// Layout, type and states only: no colour and no size here, so a variant or
// a size never has to fight the base classes (there is no class merging).
const BASE =
  'group/button inline-flex items-center justify-center gap-2 font-sans font-bold text-center whitespace-nowrap rounded-xl transition-colors duration-200 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50 disabled:pointer-events-none aria-disabled:opacity-50 aria-disabled:pointer-events-none'

// Three heights for the whole site: 36 compact, 44 default, 48 key actions.
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-sm',
  md: 'h-11 px-[1.125rem] text-[0.9375rem]',
  lg: 'h-12 px-[1.375rem] text-base',
}

// A fill that carries white text uses brand-strong: white on the brand
// accent is 4.2:1, below the 4.5:1 a label needs.
const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-strong text-white fill-white hover:bg-brand-deep',
  large: 'bg-brand-strong text-white fill-white hover:bg-brand-deep',
  outlined:
    'bg-surface text-ink fill-ink border border-solid border-ink/[0.18] hover:border-brand hover:bg-brand-soft',
  neutral: 'bg-muted-soft text-ink fill-ink hover:bg-muted-hover',
  danger:
    'bg-surface text-danger fill-danger border border-solid border-danger hover:bg-danger-soft',
  link: 'bg-transparent text-brand-ink fill-brand-ink hover:underline',
  secondary:
    // eslint-disable-next-line no-restricted-syntax -- white on a dark banner, the same in both themes
    'bg-white text-inverse fill-inverse border border-solid border-white hover:bg-transparent hover:text-white hover:fill-white',
  accent: 'bg-inverse text-white fill-white hover:bg-black',
  empty: 'bg-transparent text-ink fill-ink',
}

const Spinner = ({ label }: { label: string }) => (
  <span className="loader flex-1">
    <svg
      aria-hidden="true"
      role="status"
      className="inline w-4 h-4 animate-spin"
      viewBox="0 0 100 101"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
        fill="currentColor"
        fillOpacity="0.25"
      />
      <path
        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
        fill="currentColor"
      />
    </svg>
    <span className="sr-only">{label}</span>
  </span>
)

const Button = ({
  icon,
  link,
  disabled,
  className,
  classNameWrapper,
  children,
  type,
  variant = 'primary',
  size,
  onClick,
  target,
  rel,
  iconClassName,
  isLoading,
  iconAfter,
  ...props
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation()
  const buttonClassNames = clsx(
    BASE,
    SIZES[size ?? (variant === 'large' ? 'lg' : 'md')],
    VARIANTS[variant],
    className,
  )

  const content = !isLoading ? (
    <>
      {icon && <small className={clsx('icon-button w-3 h-3', iconClassName)}>{icon}</small>}
      {children && (
        <span
          className={clsx(
            classNameWrapper,
            'text-nowrap inline-flex gap-2  justify-center',
            iconAfter ? 'flex-1' : 'md:flex-1',
          )}
        >
          {children}
        </span>
      )}

      {iconAfter && (
        <small className={clsx('icon-button w-3 h-3', iconClassName)}>{iconAfter}</small>
      )}
    </>
  ) : (
    <Spinner label={t('select_loading')} />
  )

  if (link) {
    return (
      <Link
        rel={rel}
        target={target}
        to={link}
        className={buttonClassNames}
        onClick={(e) => onClick && onClick(e)}
        {...(isLoading || disabled ? { tabIndex: -1, 'aria-disabled': true } : {})}
        {...props}
      >
        {content}
      </Link>
    )
  }
  return (
    <button
      disabled={disabled || isLoading}
      onClick={(e) => onClick && onClick(e)}
      type={type}
      className={buttonClassNames}
      {...props}
    >
      {content}
    </button>
  )
}

export default Button
